import { AxiosError } from 'axios';
import type { TFunction } from 'i18next';

import type { useToast } from '@quick/components/Toaster';
import type { DetailError } from '@quick/go-api-types/common';
import type { CalculateServiceReq } from '@quick/go-api-types/src/services/calculate';
import type { ResProviderQuote } from '@quick/go-api-types/src/services/offers';

import { ToastActionCopy } from '@/components/core/ToastActionCopy';
import type { IForm } from '@/components/demandForm';
import { getValidData } from '@/components/demandForm/utils';
import { standardError } from '@/layers/core/deal/mappers/standardError';
import { axios_demand } from '@/layers/demand/data/fetchers/instances';

import { serializeFormParams } from './useFormParams';

interface SubmitHandlerParam {
  data: IForm;
  lang?: string;
  /** Allow request offers */
  aro?: boolean;
  /** Current quote */
  si?: number | null;
  toast: ReturnType<typeof useToast>['toast'];
  t: TFunction<['common', 'demand']>;
  token?: string | null;
  validator: (valid: CalculateServiceReq) => unknown;
  serviceAdditionals: string[];
  onlyValidate?: boolean;
  useFirstStop?: boolean;
}

export async function submitHandler({
  data,
  lang,
  aro,
  si,
  toast,
  t,
  token,
  validator,
  serviceAdditionals,
  onlyValidate,
  useFirstStop,
}: SubmitHandlerParam) {
  const valid = getValidData(data, { serviceAdditionals, useFirstStop });

  const reqConfig = {
    headers: {
      ...(token && { Authorization: token }),
      'Accept-Language': lang,
    },
  };
  try {
    const payload = validator(valid) as CalculateServiceReq;
    if (onlyValidate) return;

    // remove unwanted query params and hide offers after user submits
    window.history.pushState(
      null,
      '',
      `${window.location.pathname}${serializeFormParams(location.search, {
        // Keep form if it exists, requote, and selectedItem (quote)
        aro,
        book: null,
        uuid: null,
        su: null,
        reset: null,
      })}`
    );

    const serv = await axios_demand.post<ResProviderQuote>(
      `/services/offers`,
      payload,
      { ...reqConfig, params: { quote_id: si || undefined } }
    );

    // Add uuid
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${serializeFormParams(location.search, {
        uuid: serv.data.uuid,
      })}`
    );
  } catch (err) {
    if (
      // (window.__Q_DF || process.env.NODE_ENV === 'development') &&
      !(err instanceof AxiosError)
    )
      // eslint-disable-next-line no-console
      console.error(err);

    let msg = t('demand:demandForm.validate');
    if (err instanceof AxiosError)
      msg = t('common:msg.has_ocurred', {
        msg: standardError(
          (err as AxiosError<DetailError>).response?.data?.detail ??
            err.response?.data ??
            err.message
        ),
      });

    return (
      toast({
        variant: 'danger',
        description: msg,
        action: <ToastActionCopy msg={msg} />,
      }) && void 0
    );
  }
}
